import { gql } from '@apollo/client'

import {
  type LoadOptionsForPickListParams,
  type PickListOption,
} from '../components/FilterEditor/FilterEditor/FilterEditor.parts'
import { apolloClient } from './apolloClient'

const isSafeGraphqlName = (name: string): boolean =>
  /^[A-Za-z_][A-Za-z0-9_]*$/.test(name)

const capitalize = (s: string): string =>
  s.length === 0 ? s : s[0].toUpperCase() + s.slice(1)

const toPascal = (name: string): string =>
  name.split(/[._-]/).map(capitalize).join('')

type BuildCubeDistinctQueryArgs = {
  cubeViewName: string
  fieldKey: string
  hasSearch: boolean
  hasNotIn: boolean
}

/**
 * Builds a cube distinct / typeahead query.
 *
 * Shape matches existing cube queries (where on `cube`, member + measure):
 *   cube(limit, offset, orderBy, where: { <view>: { <field>: { contains, notIn } } }) {
 *     <view> { count, <field> }
 *   }
 *
 * Response rows are nested under `cube[].<view>.<field>` — map with
 * `CubeDistinctValues.mapResults`.
 */
const getQueryDocument = ({
  cubeViewName,
  fieldKey,
  hasSearch,
  hasNotIn,
}: BuildCubeDistinctQueryArgs) => {
  if (!isSafeGraphqlName(cubeViewName) || !isSafeGraphqlName(fieldKey)) {
    throw new Error(
      `Invalid cube distinct identifiers: view=${cubeViewName} field=${fieldKey}`,
    )
  }

  const fieldFilterParts = [
    hasSearch ? 'contains: $search' : null,
    hasNotIn ? 'notIn: $notIn' : null,
  ].filter((part): part is string => part !== null)

  const whereLine =
    fieldFilterParts.length > 0
      ? `    where: { ${cubeViewName}: { ${fieldKey}: { ${fieldFilterParts.join(', ')} } } }`
      : null

  const varDefs = [
    hasSearch ? '$search: [String!]' : null,
    hasNotIn ? '$notIn: [String!]' : null,
    '$limit: Int',
    '$offset: Int',
  ].filter((part): part is string => part !== null)

  const queryName = `${toPascal(cubeViewName)}${toPascal(fieldKey)}Distinct`
  const queryString = [
    `query ${queryName}(${varDefs.join(', ')}) {`,
    '  cube(',
    '    limit: $limit',
    '    offset: $offset',
    `    orderBy: { ${cubeViewName}: { ${fieldKey}: asc } }`,
    whereLine,
    '  ) {',
    `    ${cubeViewName} {`,
    '      count',
    `      ${fieldKey}`,
    '    }',
    '  }',
    '}',
  ]
    .filter((line): line is string => line !== null)
    .join('\n')

  return gql(queryString)
}

/**
 * Maps nested cube rows to unique pick-list options.
 * `data.cube` is `[CubeResult!]!`; each row holds `{ [view]: { [field]: string } }`.
 */
const mapResults = (
  data: unknown,
  cubeViewName: string,
  fieldKey: string,
): PickListOption[] => {
  const cube = (data as { cube?: unknown } | null | undefined)?.cube
  if (!Array.isArray(cube)) return []

  const seen = new Set<string>()
  const options: PickListOption[] = []

  for (const row of cube) {
    if (!row || typeof row !== 'object') continue
    const view = (row as Record<string, unknown>)[cubeViewName]
    if (!view || typeof view !== 'object') continue
    const raw = (view as Record<string, unknown>)[fieldKey]
    if (typeof raw !== 'string' || raw === '') continue
    if (seen.has(raw)) continue
    seen.add(raw)
    options.push({ label: raw, value: raw })
  }

  return options
}

type FetchCubeDistinctParams = {
  cubeViewName: string
  fieldKey: string
  search?: string | null
  limit?: number | null
  skip?: number | null
  notIn?: Array<string | null> | null
}

const fetchValues = async ({
  cubeViewName,
  fieldKey,
  search,
  limit,
  skip,
  notIn,
}: FetchCubeDistinctParams): Promise<PickListOption[]> => {
  const trimmed = search?.trim() ?? ''
  const excluded = (notIn ?? []).filter((v): v is string => Boolean(v))
  const hasSearch = trimmed.length > 0
  const hasNotIn = excluded.length > 0

  const result = await apolloClient.query({
    query: getQueryDocument({
      cubeViewName,
      fieldKey,
      hasSearch,
      hasNotIn,
    }),
    variables: {
      ...(hasSearch ? { search: [trimmed] } : {}),
      ...(hasNotIn ? { notIn: excluded } : {}),
      limit: limit ?? 250,
      offset: skip ?? 0,
    },
  })

  return mapResults(result.data, cubeViewName, fieldKey)
}

const createLoadOptions = () => {
  return ({
    cubeDistinctView,
    unmappedFieldKey,
    search,
    limit,
    skip,
    notIn,
  }: LoadOptionsForPickListParams): Promise<PickListOption[]> => {
    if (!cubeDistinctView || !unmappedFieldKey) {
      return Promise.resolve([])
    }

    return fetchValues({
      cubeViewName: cubeDistinctView,
      fieldKey: unmappedFieldKey,
      search,
      limit,
      skip,
      notIn,
    })
  }
}

export const CubeDistinctValues = {
  getQueryDocument,
  mapResults,
  fetch: fetchValues,
  createLoadOptions,
}
